import React from 'react';
import {
  Building2,
  ShieldCheck,
  Plug2,
  Award,
  ArrowUpRight,
  Network,
  Clock,
  KeyRound,
  Webhook,
  UserCheck,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface EnterprisePillar {
  id: string;
  icon: LucideIcon;
  tag: string;
  title: string;
  body: string;
  items: string[];
  highlights: { icon: LucideIcon; label: string }[];
  number: string;
}

const pillars: EnterprisePillar[] = [
  {
    id: 'organizacao',
    icon: Building2,
    tag: 'Organização Escalável',
    title: 'Organograma Vivo & Escopo de Dados Dinâmico',
    body:
      'Estrutura que conecta dinamicamente departamentos, cargos e gestores ao escopo de dados do sistema — do Global ao Individual — com suporte a operadores de chão de fábrica.',
    items: [
      'Organograma interativo com hierarquia de gestores e departamentos',
      'Escopo de visibilidade por cargo: Global → Departamento → Individual',
      'Acesso por e-mail corporativo ou contas baseadas em CPF',
      'Backup LGPD com exportação completa da organização',
    ],
    highlights: [
      { icon: Network, label: 'Multi-unidade' },
      { icon: UserCheck, label: 'CPF + E-mail' },
    ],
    number: '01',
  },
  {
    id: 'governanca',
    icon: ShieldCheck,
    tag: 'Governança & Capacidade',
    title: 'RBAC Rígido, Auditoria e Parametrização de Tempos',
    body:
      'Matriz RBAC de permissões por papel e módulo, logs de auditoria imutáveis, políticas de segurança avançadas e governança detalhada de capacidade operacional.',
    items: [
      'Matriz RBAC por módulo, processo e departamento',
      'Logs de auditoria imutáveis com IP, recurso e ação',
      'Políticas: 2FA, senha, sessão e IP allowlist',
      'FTE, limites de WIP, custo/hora e semana padrão por função',
    ],
    highlights: [
      { icon: KeyRound, label: 'RBAC granular' },
      { icon: Clock, label: 'Governança de Tempos' },
    ],
    number: '02',
  },
  {
    id: 'integracoes',
    icon: Plug2,
    tag: 'Ecossistema Aberto',
    title: 'API-Ready com Integrações Nativas de Mercado',
    body:
      'Arquitetura preparada para operações de missão crítica. Conectores nativos para grandes ERPs, ferramentas de colaboração, BI e webhooks para automações sem código.',
    items: [
      'Conectores nativos: SAP, TOTVS, Slack, Teams, Power BI',
      'REST API, GraphQL e documentação Swagger completa',
      'Webhooks e Event Bus para automações customizadas',
      'Marketplace de apps e Developer Portal',
    ],
    highlights: [
      { icon: Webhook, label: 'Webhooks' },
      { icon: Plug2, label: 'ERP nativo' },
    ],
    number: '03',
  },
  {
    id: 'excelencia',
    icon: Award,
    tag: 'Excelência Individual',
    title: 'Painel de Autoridade & Programa de Excelência',
    body:
      'Perfil corporativo com programa de excelência que pontua o desempenho de forma transparente. Evolução meritocrática por níveis de maturidade, medindo disciplina, projetos liderados e colaboração.',
    items: [
      'Painel de Autoridade com métricas individuais em tempo real',
      'Níveis progressivos de maturidade por desempenho comprovado',
      'Score de aderência com pesos por entrega, pontualidade e rotina',
      'Ranking meritocrático por cargo, área e histórico evolutivo',
    ],
    highlights: [
      { icon: Award, label: 'Excelência' },
      { icon: UserCheck, label: 'Score individual' },
    ],
    number: '04',
  },
];

const stackItems = [
  'Cloud-native (AWS / Azure)',
  'API-first REST & GraphQL',
  'Event-driven Architecture',
  'Zero-downtime Deployment',
  'SOC 2 Type II compliant',
  'End-to-end Encryption (AES-256 / TLS 1.3)',
];

export function SitePlatformSection() {
  return (
    <section id="platform" className="border-t border-slate-100 bg-white py-16 lg:py-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="mb-10 text-center">
          <p className="mb-4 text-sm font-bold uppercase tracking-widest text-blue-600 md:text-base">
            Infraestrutura Enterprise
          </p>
          <h2 className="mx-auto mb-5 max-w-4xl text-3xl font-extrabold leading-tight tracking-tight text-slate-900 md:text-4xl lg:text-5xl">
            4 Pilares de Confiança Corporativa para Operações de Missão Crítica.
          </h2>
          <p className="mx-auto max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
            DataFlow foi projetado do zero para ambientes corporativos complexos — com alta concorrência,
            múltiplas unidades, governança rigorosa e integrações heterogêneas. Não é uma adaptação de
            software genérico.
          </p>
        </div>

        <div className="mb-10 grid items-stretch gap-5 lg:grid-cols-2">
          {pillars.map((p) => {
            const Icon = p.icon;
            return (
              <article
                key={p.id}
                className="lp-enterprise-card relative flex flex-col overflow-hidden rounded-xl border border-slate-200/80 bg-slate-50/50 p-6 shadow-sm transition-all duration-300 hover:bg-white hover:shadow-md lg:p-7"
              >
                <span
                  className="absolute top-3 right-4 select-none text-slate-100"
                  style={{ fontSize: '4rem', fontWeight: 900, lineHeight: 1, letterSpacing: '-0.04em' }}
                >
                  {p.number}
                </span>

                <div className="relative flex flex-1 flex-col">
                  <div className="mb-5 flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className="rounded-lg border border-blue-100 bg-blue-50 p-2.5">
                        <Icon size={20} className="text-blue-600" />
                      </div>
                      <div>
                        <p
                          className="mb-1 uppercase tracking-widest text-slate-400"
                          style={{ fontSize: '0.62rem', fontWeight: 700 }}
                        >
                          {p.tag}
                        </p>
                        <h3 className="text-slate-900 leading-snug" style={{ fontWeight: 700, fontSize: '0.95rem' }}>
                          {p.title}
                        </h3>
                      </div>
                    </div>
                    <div className="hidden shrink-0 gap-1.5 sm:flex">
                      {p.highlights.map(({ icon: HIcon, label }) => (
                        <span
                          key={label}
                          className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-slate-600"
                          style={{ fontSize: '0.65rem', fontWeight: 600 }}
                        >
                          <HIcon size={10} />
                          {label}
                        </span>
                      ))}
                    </div>
                  </div>

                  <p className="mb-5 leading-relaxed text-slate-600" style={{ fontSize: '0.825rem' }}>
                    {p.body}
                  </p>

                  <ul className="grid gap-2 sm:grid-cols-2">
                    {p.items.map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-blue-500" />
                        <span className="text-slate-600 leading-snug" style={{ fontSize: '0.775rem' }}>
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            );
          })}
        </div>

        <div className="rounded-2xl border border-slate-200/80 bg-slate-50/60 p-6 shadow-sm lg:p-7">
          <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 md:text-sm">
              Stack Tecnológico
            </p>
            <button
              type="button"
              className="inline-flex w-fit items-center gap-1.5 text-blue-600 transition-colors hover:text-blue-700"
              style={{ fontSize: '0.875rem', fontWeight: 600 }}
              onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Solicitar documentação técnica completa
              <ArrowUpRight size={14} />
            </button>
          </div>
          <div className="grid gap-x-6 gap-y-2.5 sm:grid-cols-2 lg:grid-cols-3">
            {stackItems.map((item) => (
              <div key={item} className="flex items-center gap-2">
                <span className="h-1 w-1 shrink-0 rounded-full bg-blue-400" />
                <span className="text-sm font-medium text-slate-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
